/**
 * ProposalDetail Component
 *
 * Story FOS-4.2.8: ProposalList & Detail Enhancement
 * Task 4: Create ProposalDetail component
 *
 * Full proposal view with description, vote breakdown, quorum progress,
 * and "Vote Now" button for active proposals.
 *
 * @see AC-4.2.8.5 - Full proposal description and vote breakdown displayed
 * @see AC-4.2.8.6 - "Vote Now" navigates to VotingPage
 *
 * @module components/governance/ProposalDetail
 */

import { useNavigate, Link } from 'react-router-dom';
import { User, Calendar, ExternalLink, Check } from 'lucide-react';
import { VoteBreakdownChart } from './VoteBreakdownChart';
import { QuorumProgressBar } from './QuorumProgressBar';

// =============================================================================
// Types
// =============================================================================

/**
 * Proposal status as returned by the governance canister.
 *
 * BL-326: Aligned with governance.did `ProposalStatus` variant. Oracle-bridge
 * returns these strings verbatim on `/api/governance/proposals/:id`. The
 * earlier 'Voting'/'Passed'/'Cancelled' triple never matched any real wire
 * value, so `isVotingActive` was always false and the Vote Now button never
 * appeared on Active proposals.
 *
 * @see governance/src/governance.did (Draft | Active | Approved | Rejected | Executed | Failed | Invalid)
 */
export type ProposalDetailStatus =
  | 'Draft'
  | 'Active'
  | 'Approved'
  | 'Rejected'
  | 'Executed'
  | 'Failed'
  // BL-450: retroactively invalidated via the §14.5 amended-proposal path.
  | 'Invalid';

/**
 * Tailwind class map for the status badge. Mirrors the palette used in
 * `ProposalCard` for visual consistency across list and detail views.
 */
const STATUS_COLORS: Record<ProposalDetailStatus, string> = {
  Draft: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  Active: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  Approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  Rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  Executed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  Failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  // BL-450: Invalid reads as a distinct terminal state (struck-through outcome).
  Invalid: 'bg-gray-200 text-gray-800 line-through dark:bg-gray-800/50 dark:text-gray-300',
};

export interface ProposalDetailProps {
  /** Proposal ID */
  id: number;
  /** Proposal title */
  title: string;
  /** Proposal category */
  category: string;
  /** Full proposal description */
  description: string;
  /** Proposer display name */
  proposerName: string;
  /** Creation timestamp (Unix milliseconds) */
  createdAt: number;
  /** Voting end timestamp (Unix milliseconds) */
  votingEndsAt: number;
  /** Proposal status */
  status: ProposalDetailStatus;
  /** Yes votes count */
  yesVotes: number;
  /** No votes count */
  noVotes: number;
  /** Abstain votes count */
  abstainVotes: number;
  /** Needs Refinement votes count */
  needsRefinementVotes: number;
  /** Total vote count */
  totalVotes: number;
  /** Quorum threshold */
  quorumThreshold: number;
  /** Whether current user has voted */
  userHasVoted: boolean;
  /** Link to discussion thread (optional) */
  discussionLink?: string;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Format date for display using user's locale
 */
function formatDate(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

// =============================================================================
// Component
// =============================================================================

/**
 * Full proposal detail view
 */
export function ProposalDetail({
  id,
  title,
  category,
  description,
  proposerName,
  createdAt,
  votingEndsAt,
  status,
  yesVotes,
  noVotes,
  abstainVotes,
  needsRefinementVotes,
  totalVotes,
  quorumThreshold,
  userHasVoted,
  discussionLink,
}: ProposalDetailProps) {
  const navigate = useNavigate();

  // BL-326: canister status is 'Active' during the voting window. The earlier
  // 'Voting' literal never matched the wire value, so the Vote Now button was
  // permanently hidden.
  const isVotingActive = status === 'Active' && votingEndsAt > Date.now();
  const canVote = isVotingActive && !userHasVoted;

  const handleVoteClick = () => {
    navigate(`/voting/${id}`);
  };

  return (
    <div className="space-y-6" data-testid="proposal-detail">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span
            className="px-2 py-0.5 text-xs font-medium rounded bg-primary/10 text-primary"
            data-testid="proposal-detail-category"
          >
            {category}
          </span>
          {/* BL-326: status badge mirrors ProposalCard so list and detail views agree. */}
          <span
            className={`px-2 py-0.5 text-xs font-medium rounded ${STATUS_COLORS[status]}`}
            data-testid="proposal-detail-status"
          >
            {status}
          </span>
          {userHasVoted && (
            <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
              <Check className="w-3 h-3" />
              You voted
            </span>
          )}
        </div>
        <h2
          className="text-xl font-semibold text-foreground"
          data-testid="proposal-detail-title"
        >
          #{id}: {title}
        </h2>
      </div>

      {/* BL-450: invalidation notice — surfaced when a tallied proposal was
          retroactively invalidated via the §14.5 amended-proposal path. */}
      {status === 'Invalid' && (
        <div
          className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground"
          data-testid="proposal-detail-invalid-notice"
          role="note"
        >
          <span aria-hidden="true">⚠</span>
          <span>
            This proposal&rsquo;s outcome was retroactively invalidated through the
            member-protection appeal path (§14.5). Its original effects are not automatically
            reversed.
          </span>
        </div>
      )}

      {/* Meta info */}
      <div className="flex items-center gap-6 text-sm text-muted-foreground">
        <span className="flex items-center gap-1.5" data-testid="proposal-detail-proposer">
          <User className="w-4 h-4" />
          {proposerName}
        </span>
        <span className="flex items-center gap-1.5" data-testid="proposal-detail-date">
          <Calendar className="w-4 h-4" />
          {formatDate(createdAt)}
        </span>
      </div>

      {/* Description */}
      <div className="bg-muted/30 rounded-lg border border-border p-4">
        <h3 className="font-medium text-foreground mb-2">Proposal Description</h3>
        <p
          className="text-muted-foreground whitespace-pre-wrap"
          data-testid="proposal-detail-description"
        >
          {description}
        </p>
      </div>

      {/* Vote Breakdown */}
      <div className="bg-card rounded-lg border border-border p-4">
        <h3 className="font-medium text-foreground mb-4">Current Results</h3>
        <VoteBreakdownChart
          yes={yesVotes}
          no={noVotes}
          abstain={abstainVotes}
          needsRefinement={needsRefinementVotes}
        />
        <div className="mt-4">
          <QuorumProgressBar
            currentVotes={totalVotes}
            requiredVotes={quorumThreshold}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-4">
        {canVote && (
          <button
            onClick={handleVoteClick}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
            data-testid="proposal-detail-vote-btn"
          >
            Vote Now
          </button>
        )}

        {discussionLink && (
          <Link
            to={discussionLink}
            className="flex items-center gap-1.5 text-sm text-primary hover:underline"
            data-testid="proposal-detail-discussion-link"
          >
            View Discussion
            <ExternalLink className="w-4 h-4" />
          </Link>
        )}
      </div>
    </div>
  );
}

export default ProposalDetail;
